/**
 * The ONE dismissible hint of D-TSG-05 / SPEC-TENANT-SURFACES RULE-011.
 *
 * WHY IT EXISTS. The advanced switch defaults to OFF for a tenant user
 * (D-TSG-22; ON for a tenant or platform admin), which makes the platform admin's per-tenant
 * `advanced_enabled` switch look inert at the moment it is flipped: the tenant
 * has the tier, the user has the permission, and nothing on any screen changes.
 * So a permitted user who has never decided anything about the switch, and
 * whose switch reads OFF, is shown one hint naming WHERE the switch lives - the
 * "Saját beállítások" page of D-TSG-02. A user whose default is ON already sees
 * the categories and is not shown it.
 *
 * WHO NEVER SEES IT. A caller without `ui.use_advanced`, and a tenant whose
 * switch is off. RULE-011 states this as a MUST NOT and RULE-012's disclosure
 * clause is why: the hint names the tier, so showing it to someone the gate
 * refuses would tell them a tier exists that they cannot reach. It is also
 * absent while either answer is still loading, and while the preference read is
 * unresolved - `advancedModeSet` is `null` until the server says otherwise, and
 * a hint is never shown on a guess.
 *
 * HOW DISMISSAL PERSISTS. Dismissing is an explicit
 * `PUT /api/v1/me/ui-preferences {"advanced_mode": false}` (D-TSG-07 item 3).
 * That CREATES the user's row, so `advanced_mode_set` turns true and the hint
 * is gone on every device the user signs in on. There is deliberately NO
 * localStorage key and no second "dismissed" field: API-003's key set is closed
 * (a body key outside it is a 400), and a per-device dismissal would bring the
 * hint back on the next laptop.
 *
 * @module components/tenant/AdvancedModeHint
 * @see TASK-TSURF-W6-MY-SETTINGS-PAGE-CHG-011
 * @see SPEC-TENANT-SURFACES-RULE-011 / RULE-012 / UI-003 / API-002 / API-003 / SCEN-005
 * @see DECISIONS.md D-TSG-05, D-TSG-07 item 3
 */
import React from "react";
import { Alert, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Trans } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useAdvancedTier } from "../shared/AdvancedGroup";
import { useSetAdvancedMode, useUIPreferences } from "../../api/uiPreferences";

/**
 * Renders the hint, or nothing at all.
 *
 * It reads the preference through the ONE owner of the
 * `["me","ui-preferences"]` key, enabled only when the tier could open, so a
 * caller without the tier issues no request (SPEC-FRONTEND RULE-077).
 */
export function AdvancedModeHint(): React.ReactElement | null {
  const { _: t } = useLingui();
  const { advancedEnabled, holdsPermission } = useAdvancedTier();
  const permitted = advancedEnabled && holdsPermission;
  const preferences = useUIPreferences({ enabled: permitted });
  const dismiss = useSetAdvancedMode();

  // `advancedModeSet` is null while loading and on any failure. Only an
  // explicit `false` from the server means "this user has never decided"; and
  // only a switch that reads OFF needs pointing at (D-TSG-22).
  if (!permitted || preferences.advancedModeSet !== false || preferences.advancedMode) return null;

  return (
    <Alert
      severity="info"
      data-testid="advanced-mode-hint"
      sx={{ py: 0, mr: 2 }}
      action={
        <IconButton
          size="small"
          aria-label={t(msg`Dismiss this hint`)}
          disabled={dismiss.isPending}
          onClick={() => {
            // The dismissal IS the write: it creates the row with the value the
            // user already has, so advanced_mode_set turns true everywhere.
            dismiss.mutate(false);
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <Trans>
        Advanced settings are available to you. Switch them on under My settings in the account
        menu.
      </Trans>
    </Alert>
  );
}

export default AdvancedModeHint;
